import React, { useState, useEffect } from 'react';
import LoginForm from '../components/LoginForm';
import adaLogo from '../ada-logo.svg';
import { useNavigate } from 'react-router';
import { useLoading } from '../App';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const LoginPage: React.FC = () => {
  const [error, setError] = useState<string | undefined>(undefined);
  const [showVerify, setShowVerify] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendError, setResendError] = useState<string | undefined>(undefined);
  const [emailForResend, setEmailForResend] = useState('');
  const navigate = useNavigate();
  const { loading, setLoading } = useLoading();

  useEffect(() => {
    if (localStorage.getItem('access_token')) {
      navigate('/analyze', { replace: true });
    }
  }, [navigate]);

  const handleLogin = async (username: string, password: string) => {
    setLoading(true);
    setError(undefined);
    setShowVerify(false);
    setResendError(undefined);
    setResent(false);
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);
      const res = await fetch(`${API_URL}/login/`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        navigate('/analyze', { replace: true });
      } else if (data.error && data.error.toLowerCase().includes('verify')) {
        setShowVerify(true);
        setEmailForResend(username); // Assume username is email or can be used for resend
      } else {
        setError(data.error || 'Login failed.');
      }
    } catch (e) {
      setError('Network error.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setResendError(undefined);
    setResent(false);
    try {
      const res = await fetch(`${API_URL}/resend_verification/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailForResend }),
      });
      const data = await res.json();
      if (data.success) {
        setResent(true);
      } else {
        setResendError(data.error || 'Failed to resend verification email.');
      }
    } catch (e) {
      setResendError('Network error.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: '#e3eafc' }}>
      <a href="/">
        <img src={adaLogo} alt="ADA Logo" style={{ width: 72, marginBottom: 18, cursor: 'pointer' }} />
      </a>
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px #039be51a', padding: '32px 32px 24px 32px', minWidth: 320, maxWidth: 360 }}>
        <p style={{ color: '#555', fontSize: '1rem', marginBottom: 18, textAlign: 'center' }}>Welcome back! Log in to analyze and ask questions about your documents.</p>
        {showVerify ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, padding: '18px 0' }}>
            <div style={{ background: '#26c6da', color: '#fff', borderRadius: 8, padding: '18px 32px', fontWeight: 600, fontSize: '1.1rem', textAlign: 'center', marginBottom: 10 }}>
              Your account is not verified.<br />
              Please check your email and click the verification link.<br />
              <span style={{ fontWeight: 400, fontSize: '0.98rem', color: '#e3eafc', display: 'block', marginTop: 8 }}>
                (If you don't see it, check your spam folder.)
              </span>
            </div>
            <button
              onClick={handleResend}
              disabled={resendLoading}
              style={{
                background: '#039be5',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontWeight: 700,
                fontSize: '1.08rem',
                boxShadow: '0 2px 8px #29b6f633',
                cursor: resendLoading ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s',
                opacity: resendLoading ? 0.7 : 1,
                padding: '12px 0',
                minWidth: 180,
                marginBottom: 8
              }}
            >
              {resendLoading ? 'Resending...' : 'Resend verification email'}
            </button>
            {resent && (
              <div style={{ color: '#43a047', background: '#e3fcec', borderRadius: 6, padding: '8px 12px', fontWeight: 500, fontSize: '0.98rem', marginTop: 2 }}>
                Verification email resent!
              </div>
            )}
            {resendError && (
              <div style={{ color: '#d32f2f', background: '#ffeaea', borderRadius: 6, padding: '8px 12px', fontWeight: 500, fontSize: '0.98rem', marginTop: 2 }}>{resendError}</div>
            )}
            <div style={{ marginTop: 18, color: '#555', fontSize: '1rem' }}>
              Already verified? <a href="/login" style={{ color: '#039be5', fontWeight: 600, textDecoration: 'none' }}>Sign In</a>
            </div>
          </div>
        ) : (
          <LoginForm onSubmit={handleLogin} error={error} loading={loading} />
        )}
      </div>
      <div style={{ marginTop: 18, color: '#555', fontSize: '1rem' }}>
        New to ADA? <a href="/register" style={{ color: '#039be5', fontWeight: 600, textDecoration: 'none' }}>Create an account</a>
      </div>
    </div>
  );
};

export default LoginPage; 